import React from 'react';

import {ProcessClientOrigin, ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ListingRendererType,
  ColumnAccessorKey,
  ColumnConfiguration,
} from 'nav/expanded/ListingRendererConstants';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {SortKey} from 'search/SearchConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {BasicEvent} from 'util/Types';

import {ProcessTaskListingGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';

export enum ProcedureColumnKey {
  PROCEDURE_RUN = 'PROCEDURE_RUN',
  PROCEDURE_METHOD = 'PROCEDURE_METHOD',
  PROCEDURE_CONDITIONS = 'PROCEDURE_CONDITIONS',
  PROCEDURE_REPLICATE = 'PROCEDURE_REPLICATE',
  PROCEDURE_TEMPLATE = 'PROCEDURE_TEMPLATE',
}

export type ProcedureClientOrigin = ProcessClientOrigin.RECIPES | ProcessClientOrigin.ASSAYS;

export type ProcedureListingRendererType =
  | ListingRendererType.ASSAY_RUN_TASK
  | ListingRendererType.RECIPE_RUN_TASK
  | ListingRendererType.ASSAY_UNIT_OPERATION_TASK
  | ListingRendererType.RECIPE_UNIT_OPERATION_TASK
  | ListingRendererType.RECIPE_STEP_TASK
  | ListingRendererType.ASSAY_STEP_TASK;

type ProcedureColumnKeyToColumnData = Record<
  ProcedureColumnKey,
  Record<ProcedureClientOrigin, ScrollableTableColumn>
>;

function getProcedureColumnKeyToData(): ProcedureColumnKeyToColumnData {
  return {
    [ProcedureColumnKey.PROCEDURE_RUN]: {
      [ProcessClientOrigin.RECIPES]: {
        accessorKey: ColumnAccessorKey.RECIPE_CONDITION_REPLICATE_RUN,
        header: processExecutionTypeDisplayName(ProcessClientOrigin.RECIPES, ProcessExecutionType.PROCEDURE),
      },
      [ProcessClientOrigin.ASSAYS]: {
        accessorKey: ColumnAccessorKey.ASSAY_CONDITION_REPLICATE_RUN,
        header: processExecutionTypeDisplayName(ProcessClientOrigin.ASSAYS, ProcessExecutionType.PROCEDURE),
      },
    },
    [ProcedureColumnKey.PROCEDURE_METHOD]: {
      [ProcessClientOrigin.RECIPES]: {
        accessorKey: ColumnAccessorKey.RECIPE_UNIT_OPERATION,
        header: processExecutionTypeDisplayName(
          ProcessClientOrigin.RECIPES,
          ProcessExecutionType.PROCEDURE_METHOD
        ),
      },
      [ProcessClientOrigin.ASSAYS]: {
        accessorKey: ColumnAccessorKey.ASSAY_UNIT_OPERATION,
        header: processExecutionTypeDisplayName(
          ProcessClientOrigin.ASSAYS,
          ProcessExecutionType.PROCEDURE_METHOD
        ),
      },
    },
    [ProcedureColumnKey.PROCEDURE_CONDITIONS]: {
      [ProcessClientOrigin.RECIPES]: {
        accessorKey: ColumnAccessorKey.RECIPE_CONDITIONS,
        header: 'Condition',
      },
      [ProcessClientOrigin.ASSAYS]: {
        accessorKey: ColumnAccessorKey.ASSAY_CONDITIONS,
        header: 'Assay run',
      },
    },
    [ProcedureColumnKey.PROCEDURE_REPLICATE]: {
      [ProcessClientOrigin.RECIPES]: {
        accessorKey: ColumnAccessorKey.RECIPE_REPLICATE,
        header: 'Replicate',
      },
      [ProcessClientOrigin.ASSAYS]: {
        accessorKey: ColumnAccessorKey.ASSAY_REPLICATE,
        header: 'Assay run replicate',
      },
    },
    [ProcedureColumnKey.PROCEDURE_TEMPLATE]: {
      [ProcessClientOrigin.RECIPES]: {
        accessorKey: ColumnAccessorKey.RECIPE_TEMPLATE,
        header: 'Recipe',
        isSortable: true,
        sortKey: SortKey.SORT_RECIPE_TEMPLATE_NAME,
      },
      [ProcessClientOrigin.ASSAYS]: {
        accessorKey: ColumnAccessorKey.ASSAY_TEMPLATE,
        header: 'Assay',
        isSortable: true,
        sortKey: SortKey.SORT_ASSAY_TEMPLATE_NAME,
      },
    },
  };
}

export function getProcedureColumnData(
  columnKey: ProcedureColumnKey,
  processClientOrigin: ProcedureClientOrigin
): ScrollableTableColumn {
  return getProcedureColumnKeyToData()[columnKey][processClientOrigin];
}

export abstract class ProcedureListingRenderer<
  T extends ProcedureListingRendererType,
> extends BaseListingRendererWithPlugins<T> {
  clientOrigin: ProcedureClientOrigin;

  constructor(clientOrigin: ProcedureClientOrigin) {
    super();
    this.clientOrigin = clientOrigin;
  }

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon="process-task" className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<ProcessTaskListingGraphQL>,
    extras: unknown = {}
  ): Array<ColumnConfiguration<ProcessTaskListingGraphQL>> {
    return [];
  }

  getNonPluginRowContentHeight(item: ProcessTaskListingGraphQL, extras: unknown): number {
    return -1;
  }

  openContextMenu(item: ProcessTaskListingGraphQL, e: BasicEvent, extras: unknown): void {}
}
